import { useEffect, useRef, useState } from "react";

import { searchAddress } from "../../lib/dashboardApi";
import type { PhotonResult } from "../../lib/dashboardApi";

interface AddressAutocompleteProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

export function AddressAutocomplete({
  value,
  placeholder = "Start typing an address...",
  onChange,
}: AddressAutocompleteProps) {
  const [results, setResults] = useState<PhotonResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleInput(next: string) {
    onChange(next);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (next.length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    setOpen(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await searchAddress(next);
        setResults(list);
      } finally {
        setSearching(false);
      }
    }, 250);
  }

  return (
    <div className="addr-wrap" ref={wrapRef}>
      <input
        className="form-input"
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => handleInput(e.target.value)}
        onFocus={() => {
          if (value.length >= 3) setOpen(true);
        }}
      />
      {open && (results.length > 0 || searching) && (
        <div className="addr-suggestions">
          {searching && results.length === 0 ? (
            <div className="addr-searching">Searching…</div>
          ) : (
            results.map((r, i) => (
              <button
                type="button"
                key={i}
                className="addr-item"
                onClick={() => {
                  onChange(r.display);
                  setOpen(false);
                }}
                style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", fontFamily: "inherit", color: "var(--text)" }}
              >
                {r.display}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
